import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { Form } from 'react-apoidea';
import ReCAPTCHA from 'react-google-recaptcha';

import UserType from 'src/shared/config/UserType';
import globalActions from 'src/shared/store/global/actions';
import AccountAPI from 'src/shared/services/api/AccountAPI';
import GaService from 'src/services/GaService';
import GaCategory from 'src/config/GaCategory';
import GaAction from 'src/config/GaAction';
import Message from 'src/shared/config/Message';
import ErrorService from 'src/shared/services/ErrorService';
import common from 'src/shared/utils/common';
import localeUtils from 'src/shared/utils/localeUtils';
import withNoAuth from 'src/shared/utils/withNoAuth';
import getStepOneFields from './utils/getStepOneFields';
import getStepTwoFields from './utils/getStepTwoFields';
import getStepThreeFields from './utils/getStepThreeFields';
import getNetWorthOptions from './utils/getNetWorthOptions';
import Title from './components/Title';
import Description from './components/Description';
import SubmitButton from './components/SubmitButton';

import styles from './styles.module.scss';
import useResize from 'src/shared/hooks/useResize';
import BasePasswordInput from 'src/shared/form/components/BasePasswordInput';
import FieldTitle from './components/FieldTitle';
import HjEvent from 'src/config/HjEvent';

const LAST_STEP = 3;
const BREAKPOINT = 768;
const INIT_TIME = 60;

const RegistrationForm = (props) => {
    const { step, isInvestor, onStepChange, onFormTypeChange, onRegister } = props;
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { screenSize } = useResize();

    const reCaptchaRef = useRef();
    const [isSmallWindow, setIsSmallWindow] = useState(document.body.clientWidth < BREAKPOINT);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [time, setTime] = useState(INIT_TIME);
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    const [formData, setFormData] = useState(step === 1 ? { countryCode: localeUtils.getDefaultPhoneCountryCode() } : {});
    const [subFormData, setSubFormData] = useState({});
    const [password, setPassword] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const netWorthOptions = getNetWorthOptions(t);

    useEffect(() => {
        setIsSmallWindow(screenSize.width <= BREAKPOINT);

        return () => setIsSmallWindow(false);
    }, [screenSize.width]);

    // Count down timer for pass code resend
    useEffect(() => {
        if (step !== 2) {
            return () => {};
        }

        const countdownTimer = setTimeout(() => {
            setTime(Math.max(time - 1, 0));
        }, 1000);

        return () => clearTimeout(countdownTimer);
    });

    // Reset timer
    useEffect(() => {
        if (step !== 2) {
            return () => {};
        }
        setTime(INIT_TIME);

        return () => setTime(0);
    }, [step]);

    // Dis/Enable resend button
    useEffect(() => {
        const shouldDisable = time > 0;
        if (shouldDisable && isResendDisabled) {
            return () => {};
        }
        setIsResendDisabled(shouldDisable);

        return () => setIsResendDisabled(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    const requestOTP = async (data) => {
        const reCaptchaToken = await reCaptchaRef.current.executeAsync();
        const resp = await AccountAPI.requestOTP({
            ...data,
            recaptcha: reCaptchaToken,
        });

        reCaptchaRef.current.reset();

        if (!resp.status) {
            dispatch(
                globalActions.showAlertDialog({
                    text: resp.message || t(Message.NETWORK_ERROR),
                })
            );
        }
        return resp.status;
    };

    const onClickLoginButton = () => {
        GaService.callEvent(GaCategory.register, GaAction.clickLoginButton, '');
    };

    const onRegisterPageRedirectClick = () => {
        onFormTypeChange(isInvestor ? UserType.ADVISOR : UserType.INVESTOR);
    };

    const onBackToPrevClick = () => {
        onStepChange(step - 1 || 1);
        setSubFormData({
            ...subFormData,
            passcode: '',
        });
    };

    const onResendClick = useCallback(() => {
        if (isResendDisabled) {
            return;
        }
        requestOTP(formData);
        setTime(INIT_TIME);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isResendDisabled]);

    const onPasswordChange = ({ target: { value } }) => setPassword(value);

    const onPasswordValidityChange = (isValid) => isPasswordValid !== isValid && setIsPasswordValid(isValid);

    const onSubmitted = (currentStep, data = {}) => {
        const fullFormData = {
            ...formData,
            ...data,
        };

        if (currentStep === LAST_STEP) {
            return onRegister(fullFormData);
        }

        onStepChange(Math.min(currentStep + 1, LAST_STEP));
        setFormData(fullFormData);
        return true;
    };

    const onSubmit = async ({ formData: rawData }) => {
        let requestData;
        if (isSubmitting || !reCaptchaRef.current) {
            return false;
        }

        setIsSubmitting(true);

        if (step === 1) {
            const massagedData = {
                ...rawData,
                countryCode: rawData.phone.code,
                phoneNumber: rawData.phone.number,
            };
            delete massagedData.phone;

            // Click Create Account button
            if (window.lintrk) {
                window.lintrk('track', { conversion_id: 7681124 });
            }
            if (window.hj) {
                window.hj('event', [HjEvent.clickCreateAccountButton]);
            }
            GaService.callEvent(GaCategory.register, GaAction.clickSendOneTimePasscodeButton, '');
            if (await requestOTP(massagedData)) {
                requestData = massagedData;
            }
        } else if (step === 2) {
            // Click Submit OTP button
            if (window.lintrk) {
                window.lintrk('track', { conversion_id: 7681132 });
            }
            if (window.hj) {
                window.hj('event', [HjEvent.clickConfirmOneTimePasscodeButton]);
            }
            GaService.callEvent(GaCategory.register, GaAction.clickConfirmOneTimePasscodeButton, '');
            const resp = await AccountAPI.verifyOTP({
                ...formData,
                ...subFormData,
            });

            if (resp.status && resp.data && resp.data.verified) {
                requestData = subFormData;
            } else if (resp.status && resp.data && !resp.data.verified) {
                dispatch(
                    globalActions.showAlertDialog({
                        text: t(Message.PASSCODE_INVALID),
                    })
                );
            } else {
                const error = ErrorService.getError(resp);
                dispatch(globalActions.handleErrorDialog(error));
            }
        } else if (step === 3) {
            // Click Account Confirm button
            if (window.lintrk) {
                window.lintrk('track', { conversion_id: 7681116 });
            }

            const massagedData = {
                ...rawData,
                recaptcha: await reCaptchaRef.current.executeAsync(),
                receiveUpdates: !rawData.notReceiveUpdates,
                password,
            };
            delete massagedData.notReceiveUpdates;
            delete massagedData.email;

            if (massagedData.netWorth === netWorthOptions[0].value) {
                dispatch(
                    globalActions.showAlertDialog({
                        text: t('liquid_net_worth_error_msg'),
                    })
                );
            } else {
                requestData = massagedData;
            }
        }

        let result = false;
        if (requestData) {
            result = await onSubmitted(step, requestData);
            reCaptchaRef.current.reset();
        }
        setIsSubmitting(false);
        return result;
    };

    const onValidate = (errObj) => {
        if (common.checkFormIsInvalid(errObj)) {
            GaService.callEvent(GaCategory.register, GaAction.registerFailed, JSON.stringify(common.convertValidateObj(errObj)));
        }
    };

    const onChange = ({ formData: data }) => {
        if (step !== 2) {
            return;
        }
        setSubFormData(data);
    };

    // For render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stepOneFields = useMemo(() => getStepOneFields(t, i18n.language), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stepTwoFields = useMemo(
        () => getStepTwoFields(t, time, subFormData, isResendDisabled, isSubmitting, onResendClick),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [time, subFormData, isResendDisabled, isSubmitting]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stepThreeFields = useMemo(() => getStepThreeFields(t, isInvestor), [isInvestor]);
    let fields;
    switch (step) {
        case 2:
            fields = stepTwoFields;
            break;
        case 3:
            fields = stepThreeFields;
            break;
        default:
            fields = stepOneFields;
    }

    const shouldDescAtBottom = step === 2 && isSmallWindow;
    const formDataForRender = useMemo(
        () => ({
            ...formData,
            phone: {
                code: formData.countryCode,
                number: formData.phoneNumber,
            },
        }),
        [formData]
    );

    const renderDescription = () => {
        if (step === 3 && isSmallWindow) {
            // Should hide the description
            return null;
        }

        return (
            <Description
                step={step}
                isInvestor={isInvestor}
                onClickLoginButton={onClickLoginButton}
                onBackToPrevClick={onBackToPrevClick}
                onRegisterPageRedirectClick={onRegisterPageRedirectClick}
            />
        );
    };

    const renderSubmitButton = (submitFunc) => {
        let text;
        let id;
        switch (step) {
            case 2:
                text = t('submit_button');
                id = 'registration__submit-otp-buttom';
                break;
            case 3:
                text = t('confirm');
                id = 'registration__account-confirm-button';
                break;
            default:
                text = t('create-account-button');
                id = 'registration__create-account-button';
        }

        return (
            <SubmitButton
                className={styles.submitButton}
                id={id}
                label={text}
                disable={step === 3 && !isPasswordValid}
                loading={isSubmitting}
                onSubmit={submitFunc}
            />
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.titleAndDescContainer}>
                <Title step={step} isInvestor={isInvestor} />
                {!shouldDescAtBottom && renderDescription()}
            </div>
            <div className={styles.formContainer}>
                {/* Workaround for replacing password field without revamp the whole page */}
                {step === 3 && (
                    <div className={styles.passwordFieldContainer}>
                        <FieldTitle title={t('form_password')} />
                        <BasePasswordInput
                            isNewPassword
                            name="password"
                            placeholder={t('set_password')}
                            colorTheme="dark"
                            onChange={onPasswordChange}
                            onValidityChange={onPasswordValidityChange}
                        />
                    </div>
                )}
                <Form
                    className={`${styles.form} ${styles[`formStep${step}`]}`}
                    fields={fields}
                    submitRenderer={renderSubmitButton}
                    onChange={onChange}
                    onSubmit={onSubmit}
                    onValidate={onValidate}
                    formData={formDataForRender}
                />
            </div>
            {shouldDescAtBottom && renderDescription()}
            <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef} />
        </div>
    );
};

RegistrationForm.propTypes = {
    step: PropTypes.oneOf([1, 2, 3]).isRequired,
    isInvestor: PropTypes.bool.isRequired,
    onRegister: PropTypes.func.isRequired,
    onStepChange: PropTypes.func.isRequired,
    onFormTypeChange: PropTypes.func.isRequired,
};

export default withNoAuth()(RegistrationForm);
